import React, { useContext } from 'react'
import { StepperReturn } from 'selfServicePortal/components/Stepper/use-stepper'
import { Button } from 'selfServicePortal/components/ui/Button'
import { Card } from 'selfServicePortal/components/ui/Card'
import { useTranslation } from 'react-i18next'
import { cashToAccountFinal } from 'selfServicePortal/services/CashToAccount/final'
import SspContext from 'selfServicePortal/contexts/SspContext'
import { useMutation } from '@tanstack/react-query'
import { getCountryA3, getCurrency } from 'shared'
import { SpinnerCard } from 'selfServicePortal/components/ui/Spinner'
import { ICreditBalance } from 'selfServicePortal/interfaces/i-api-creaditCard'
import { StepThreeStatus } from './StepThree'

interface StepTwoProps {
	stepper: StepperReturn
	finalState: {
		amount: number
		referenceAccountIban: string
		referenceAccountBic?: string | undefined
	}

	onSettled: (status: StepThreeStatus) => void
}

export function StepTwo({ stepper, finalState, onSettled }: StepTwoProps) {
	const { t } = useTranslation()
	const { customerData, overviewData } = useContext(SspContext)
	const cashToAccountFinalMutation = useMutation({
		mutationFn: () =>
			cashToAccountFinal({
				amount: finalState.amount,
				referenceAccountIban: finalState.referenceAccountIban,
				nameOnReferenceAccount: `${customerData?.firstName} ${customerData?.lastName}`,
				partyId: String(customerData?.customerId),
				accountAgreementId: String(overviewData?.accountAgreementId),
				market: getCountryA3(),
			}),
		onSuccess: ({ state, message }) => {
			if (state === 'Denied') {
				onSettled({ state, message: message || '' })
			} else {
				onSettled({ state: 'Approved', message: message || '' })
			}
			stepper.nextStep()
		},
		onError: () => {
			onSettled({
				state: 'error',
				message: 'c2a-decision-undefined-failure',
			})
			stepper.nextStep()
		},
	})

	if (cashToAccountFinalMutation.isLoading) {
		return <SpinnerCard />
	}

	return (
		<Card>
			<Card.Header>{t('cashToAccountStepTwo')}</Card.Header>

			<Card.Content>
				<Card.InfoContainer>
					<Card.InfoTitle>
						{t('cashToAccountStepTwoInfoTitle')}
					</Card.InfoTitle>

					<Card.Info>
						<Card.InfoLabel>
							{t('cashToAccountAccountHolder')}
						</Card.InfoLabel>
						<Card.InfoValue>
							{`${customerData?.firstName} ${customerData?.lastName}`}
						</Card.InfoValue>
					</Card.Info>
					{finalState.referenceAccountBic ? (
						<Card.Info>
							<Card.InfoLabel>
								{t('cashToAccountBic')}
							</Card.InfoLabel>
							<Card.InfoValue>
								{finalState.referenceAccountBic}
							</Card.InfoValue>
						</Card.Info>
					) : null}
					<Card.Info>
						<Card.InfoLabel>
							{t('cashToAccountIban')}
						</Card.InfoLabel>
						<Card.InfoValue>
							{finalState.referenceAccountIban}
						</Card.InfoValue>
					</Card.Info>
					<br />
					<Card.Info>
						<Card.InfoLabel>
							{t('cashToAccountAvailableLimit')}
						</Card.InfoLabel>
						<Card.InfoValue>
							{`${(overviewData as ICreditBalance)
								?.availableCreditAmount} ${getCurrency()}`}
						</Card.InfoValue>
					</Card.Info>
					<Card.Info>
						<Card.InfoLabel>
							{t('cashToAccountRequestedAmountIs')}
						</Card.InfoLabel>
						<Card.InfoValue>{`${
							finalState.amount
						} ${getCurrency()}`}</Card.InfoValue>
					</Card.Info>
				</Card.InfoContainer>
			</Card.Content>

			<Card.Footer>
				<Button
					variant="primary"
					onClick={() => cashToAccountFinalMutation.mutate()}>
					{t('cashToAccountConfirm')}
				</Button>
				<Button variant="outline" onClick={stepper.prevStep}>
					{t('cashToAccountChange')}
				</Button>
			</Card.Footer>
		</Card>
	)
}
